import React, { useState, useEffect, useCallback, useRef } from 'react';
import { LoadingSpinner, EmptyState } from '../../../components/layout/SharedComponents';
import api from '../../../services/api';
import { toast } from 'sonner';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Switch } from '../../../components/ui/switch';
import { Textarea } from '../../../components/ui/textarea';
import { Badge } from '../../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../../components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../../components/ui/table';
import { useExpenditureLabels, useDecimalFormat } from '../../../contexts/ExpenditureLabelsContext';
import DataLookupField from '../../../components/DataLookupField';
import {
  Plus, Edit, Trash2, Save, Search, Download, X,
  FileText, Users, ChevronRight, ChevronDown, Bell,
  Lock, Eye, Pencil, Settings, Package
} from 'lucide-react';

export function ExpenseManagementPanel() {
  const [settings, setSettings] = useState(null);
  const [eligibleUsers, setEligibleUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => { fetchSettings(); }, []);
  const fetchSettings = async () => {
    setLoading(true);
    try {
      const res = await api.get('/api/admin/expense-settings');
      setSettings(res.data.settings || { account_manager: '', pre_approved_expense: false });
      setEligibleUsers(res.data.eligible_users || []);
    } catch (err) { setSettings({ account_manager: '', pre_approved_expense: false }); }
    setLoading(false);
  };
  const handleSave = async () => {
    setSaving(true);
    try { await api.put('/api/admin/expense-settings', settings); toast.success('Expense settings saved'); }
    catch (err) { toast.error(err.response?.data?.detail || 'Save failed'); }
    setSaving(false);
  };
  const update = (f, v) => setSettings(p => ({ ...p, [f]: v }));
  if (loading) return <LoadingSpinner />;

  return (
    <div className="max-w-4xl" data-testid="expense-management-panel">
      <h1 className="text-2xl font-bold mb-1">Expense Management</h1>
      <p className="text-lg font-medium text-muted-foreground mb-6">Switches</p>

      <div className="border rounded-lg p-6 mb-6" data-testid="exp-switches-section">
        <p className="text-sm text-muted-foreground mb-6">
          <strong>Note:</strong> Please select one of the users from this list as your Expense Account Approver. The selected user will get all the expense approval emails.
        </p>
        <div className="space-y-6">
          <div className="flex items-center gap-4">
            <Label className="w-40 text-sm">Account Manager</Label>
            <select value={settings.account_manager || ''} onChange={e => update('account_manager', e.target.value)}
              className="w-52 h-9 rounded-md border border-input bg-background px-3 text-sm" data-testid="exp-account-manager">
              <option value="">-- Select --</option>
              {eligibleUsers.map(u => <option key={u.user_id} value={u.user_id}>{u.name}</option>)}
            </select>
          </div>
          <div className="flex items-start gap-4 border-t pt-4">
            <Label className="w-40 text-sm pt-1">Pre-Approved Expense</Label>
            <Switch checked={settings.pre_approved_expense} onCheckedChange={v => update('pre_approved_expense', v)} data-testid="exp-pre-approved" />
            <span className={`text-xs font-medium ${settings.pre_approved_expense ? 'text-green-600' : 'text-muted-foreground'}`}>
              {settings.pre_approved_expense ? 'Yes' : 'No'}
            </span>
            <p className="text-xs text-muted-foreground flex-1">
              With the activation of this switch, users will have the ability to generate a fresh "Pre-Approved Expense". Users can specify a pre-approved budget for the expense and submit it to an Expense Approver for authorisation. Upon approval, the user can add expense items and submit them to the Account Manager for a final review, thus concluding the entire process.
            </p>
          </div>
        </div>
      </div>
      <div className="flex justify-end">
        <Button onClick={handleSave} disabled={saving} data-testid="expense-settings-save-btn">
          <Save className="h-4 w-4 mr-1" />{saving ? 'Saving...' : 'Save'}
        </Button>
      </div>
    </div>
  );
}
